import React from 'react';
import { FaExclamationTriangle, FaTimes, FaTrash, FaInfoCircle } from 'react-icons/fa';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  icon?: React.ReactNode;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Emin misiniz?',
  message = 'Bu işlemi gerçekleştirmek istediğinizden emin misiniz?',
  confirmText = 'Evet',
  cancelText = 'İptal',
  type = 'danger',
  icon,
}) => {
  if (!isOpen) {return null;}

  const typeConfig = {
    danger: {
      bgGradient: 'bg-gradient-to-br from-red-50 via-white to-red-50',
      iconBg: 'bg-gradient-to-br from-red-500 to-red-600',
      iconColor: 'text-white',
      borderColor: 'border-red-200',
      glowColor: 'shadow-red-500/25',
      confirmBg:
        'bg-gradient-to-r from-red-500 via-red-600 to-red-700 hover:from-red-600 hover:via-red-700 hover:to-red-800',
      pulseColor: 'animate-pulse-red',
      defaultIcon: <FaTrash className="drop-shadow-sm" />,
    },
    warning: {
      bgGradient: 'bg-gradient-to-br from-yellow-50 via-white to-orange-50',
      iconBg: 'bg-gradient-to-br from-yellow-500 to-orange-600',
      iconColor: 'text-white',
      borderColor: 'border-yellow-200',
      glowColor: 'shadow-yellow-500/25',
      confirmBg:
        'bg-gradient-to-r from-yellow-500 via-orange-500 to-orange-600 hover:from-yellow-600 hover:via-orange-600 hover:to-orange-700',
      pulseColor: 'animate-pulse-yellow',
      defaultIcon: <FaExclamationTriangle className="drop-shadow-sm" />,
    },
    info: {
      bgGradient: 'bg-gradient-to-br from-blue-50 via-white to-indigo-50',
      iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
      iconColor: 'text-white',
      borderColor: 'border-blue-200',
      glowColor: 'shadow-blue-500/25',
      confirmBg:
        'bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 hover:from-blue-600 hover:via-blue-700 hover:to-indigo-700',
      pulseColor: 'animate-pulse-blue',
      defaultIcon: <FaInfoCircle className="drop-shadow-sm" />,
    },
  };

  const config = typeConfig[type];
  const displayIcon = icon || config.defaultIcon;

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-lg z-50 flex items-center justify-center p-4 animate-fadeIn">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/60 to-black/40" />

      {/* Main Modal */}
      <div
        className={`
        relative ${config.bgGradient} rounded-3xl shadow-2xl ${config.glowColor} 
        border-2 ${config.borderColor} max-w-lg w-full overflow-hidden 
        transform transition-all duration-500 animate-slideInScale backdrop-blur-sm
      `}
      >
        {/* Decorative top border */}
        <div className={`h-1 ${config.iconBg}`} />

        {/* Header Section with Large Icon */}
        <div className="relative px-8 py-12 text-center">
          {/* Floating Icon with Glow Effect */}
          <div className="relative mb-8">
            <div
              className={`
              w-28 h-28 ${config.iconBg} rounded-full flex items-center justify-center mx-auto
              shadow-2xl ${config.glowColor} transform transition-all duration-300
              hover:scale-110 border-4 border-white/20 backdrop-blur-sm
            `}
            >
              <div
                className={`${config.iconColor} text-4xl transform transition-transform duration-300 hover:rotate-12`}
              >
                {displayIcon}
              </div>
            </div>

            {/* Pulse ring effect */}
            <div
              className={`
              absolute inset-0 w-28 h-28 mx-auto rounded-full 
              ${config.iconBg} opacity-20 animate-ping
            `}
            />
          </div>

          {/* Title with better typography */}
          <h3 className="text-3xl font-bold text-gray-900 mb-4 leading-tight tracking-tight">
            {title}
          </h3>

          {/* Message with better styling */}
          <p className="text-gray-600 leading-relaxed text-lg max-w-md mx-auto font-medium">
            {message}
          </p>

          {/* Close Button - More elegant */}
          <button
            onClick={onClose}
            className="absolute right-6 top-6 w-12 h-12 flex items-center justify-center rounded-full
                     bg-white/80 hover:bg-white backdrop-blur-sm transition-all duration-200
                     shadow-lg hover:shadow-xl group border border-gray-200"
          >
            <FaTimes className="text-gray-500 group-hover:text-gray-700 transition-colors text-lg" />
          </button>
        </div>

        {/* Action Buttons Section */}
        <div className="bg-gradient-to-r from-gray-50/80 to-gray-100/80 backdrop-blur-sm px-8 py-8 flex gap-4">
          {/* Cancel Button */}
          <button
            onClick={onClose}
            className="flex-1 px-8 py-5 bg-white/90 backdrop-blur-sm border-2 border-gray-200
                     text-gray-700 rounded-2xl font-bold text-lg hover:bg-gray-50
                     hover:border-gray-300 transition-all duration-200 shadow-lg hover:shadow-xl
                     transform hover:scale-105 active:scale-95"
          >
            {cancelText}
          </button>

          {/* Confirm Button */}
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className={`
              flex-1 px-8 py-5 text-white rounded-2xl font-bold text-lg shadow-2xl 
              transition-all duration-300 transform hover:scale-105 active:scale-95
              ${config.confirmBg} hover:shadow-2xl
              border-2 border-white/20 backdrop-blur-sm
              relative overflow-hidden group
            `}
          >
            {/* Button shine effect */}
            <div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent
                          transform -skew-x-12 -translate-x-full group-hover:translate-x-full
                          transition-transform duration-1000"
            />
            <span className="relative z-10 drop-shadow-sm">{confirmText}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
